import React, { useEffect, useState } from 'react'

import styled from 'styled-components'
import { FiLock, FiMail } from 'react-icons/fi'
import PhoneInput from 'react-phone-input-2'
import DatePicker from 'react-datepicker'

import 'react-phone-input-2/lib/style.css'

import PasswordValidator from './PasswordValidator'

export const Input = ({
  title,
  onChange,
  value,
  error,
  required,
  fontSize,
  borderRadius,
  type = 'email',
  IconComponent = <FiMail />,
}) => {
  const [newInput, setInput] = useState(false)

  const handleInput = (e) => {
    if (e.target.value !== '') {
      setInput(true)
    } else {
      setInput(false)
    }
    onChange(e.target.value)
  }

  useEffect(() => {
    value !== '' && setInput(true)
  }, [])

  return (
    <StyledInput error fontSize={fontSize} borderRadius={borderRadius}>
      {IconComponent}
      <input
        value={value}
        required={required}
        onChange={handleInput}
        id="email"
        type={type}
      />
      <label className={newInput ? 'active' : ''} htmlFor="text1">
        {title} {required && <span style={{ color: 'red' }}>*</span>}
      </label>
    </StyledInput>
  )
}

export const PasswordInput = ({
  title,
  onChange,
  value,
  error,
  noValidation,
}) => {
  const [newInput, setInput] = useState(false)
  const [show, setShow] = useState(false)
  const [level, setLevel] = useState(0)
  const [updated, setUpdated] = useState(false)
  const [state, setState] = useState({
    password: '',
    passwordLength: 0,
    containsNumber: 0,
    isUppercase: 0,
    containsAlphabet: 0,
  })

  // handle input change
  const handleInput = (e) => {
    const targetPassword = e.target.value

    if (targetPassword !== '') {
      setInput(true)
    } else {
      setInput(false)
    }

    setState({
      password: targetPassword,
      passwordLength: targetPassword.length > 7 ? 1 : 0,
      containsNumber: targetPassword.match('^(?=.*[0-9])') ? 1 : 0,
      containsAlphabet: targetPassword.match('^(?=.*[a-zA-Z])') ? 1 : 0,
      isUppercase: targetPassword.match('^(?=.*[A-Z])') ? 1 : 0,
    })

    onChange(targetPassword)

    setUpdated(true)
  }

  // update strength level
  useEffect(() => {
    setLevel(
      state.containsAlphabet +
        state.isUppercase +
        state.containsNumber +
        state.passwordLength
    )
    setUpdated(false)
  }, [updated])

  return (
    <StyledInput error className="password">
      <FiLock className="icon" />
      <input
        onChange={handleInput}
        value={value}
        id="password"
        type={show ? 'text' : 'password'}
      />
      <label className={newInput ? 'active' : ''} htmlFor="text1">
        {title} <span style={{ color: 'red' }}>*</span>
      </label>
      <span onClick={() => setShow(!show)} className="choose">
        {show ? 'Hide' : 'Show'}
      </span>
      {level > 0 && !noValidation ? <PasswordValidator level={level} /> : null}
    </StyledInput>
  )
}

export const PhoneNumberInput = ({
  title,
  country,
  value,
  onChange,
  required,
  wrapperClass,
  subText,
}) => {
  return (
    <StyledPhoneNumberInput>
      <label htmlFor="phoneInput" className={wrapperClass}>
        {title} {required && <span>*</span>}
      </label>
      <PhoneInput
        id="phoneInput"
        containerClass="tel__wrapper"
        buttonClass={wrapperClass}
        inputClass={'tel__input' + (wrapperClass ? ` ${wrapperClass}` : '')}
        country={country}
        value={value}
        onChange={onChange}
      />
      <sub style={{ color: 'red' }}>{subText}</sub>
    </StyledPhoneNumberInput>
  )
}

export const StandardInput = ({
  title,
  value,
  onChange,
  required,
  type,
  marginBottom = '1rem',
  wrapperClass,
  subText,
  placeholder,
}) => {
  return (
    <StyledStandardInput marginBottom={marginBottom} className={wrapperClass}>
      <label>
        {title} {required && <span>*</span>}
      </label>
      <input
        className="error"
        placeholder={placeholder}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      ></input>
      <sub style={{ color: 'red' }}>{subText}</sub>
    </StyledStandardInput>
  )
}

export const DatepickerInput = ({
  value,
  onChange,
  dateFormat,
  showMonthYearPicker,
  title,
  wrapperClass,
  subText,
}) => {
  return (
    <StyledDatePickerInput className={wrapperClass}>
      <label>{title}</label>
      <DatePicker
        selected={value}
        onChange={onChange}
        dateFormat={dateFormat}
        showYearDropdown
        scrollableYearDropdown
        showMonthYearPicker={showMonthYearPicker}
      />
      <sub style={{ color: 'red' }}>{subText}</sub>
    </StyledDatePickerInput>
  )
}

const StyledInput = styled.div`
  width: 100%;
  height: 3rem;
  position: relative;
  border: ${(p) => (p.error ? `1px solid #DC2929` : `1px solid #aeb3c9`)};
  border: 1px solid #aeb3c9;
  border-radius: ${(p) => (p.borderRadius ? p.borderRadius : '100px')};
  transition: all 0.3s ease-in-out;
  margin-bottom: 1.5rem;

  &.password {
    margin-bottom: 2.5rem;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 1.5rem;
    transform: translateY(-50%);
    font-size: 1rem;
    color: #606a93;
  }

  .choose {
    font-size: 0.625rem;
    font-weight: 600;
    color: #fd2055;
    position: absolute;
    top: 50%;
    right: 1.2rem;
    transform: translateY(-50%);
    cursor: pointer;
  }

  input {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background-color: transparent;
    font-size: 0.75rem;
    color: #000;
    padding: 0 2rem;
    padding-left: 3.5rem;
    padding-right: 3.5rem;
    border-radius: 100px;

    &:focus {
      color: #000;
      background-color: transparent;
    }
  }

  label {
    position: absolute;
    top: 50%;
    left: 3rem;
    transform: translateY(-50%);
    color: #31374f;
    transition: all 0.3s ease-in-out;
    font-size: ${(props) => (props.fontSize ? props.fontSize : '0.75rem')};
  }

  &:hover,
  &:focus-within {
    border-color: #606a93;

    input {
      color: #000;

      &:not(:placeholder-shown) ~ label {
        color: #606a93;
        padding: 0 8px;
        background-color: #fff;
        top: 0;
        left: 1rem;
      }
    }

    label {
      color: #606a93;
      padding: 0 8px;
      background-color: #fff;
      top: 0;
      left: 1rem;
      font-size: ${(props) => (props.fontSize ? props.fontSize : '0.625rem')};
    }
  }

  .active {
    color: #606a93;
    padding: 0 8px;
    background-color: #fff;
    top: 0;
    left: 1rem;
    font-size: ${(props) => (props.fontSize ? props.fontSize : '0.625rem')};
  }
`

const StyledPhoneNumberInput = styled.div`
  label {
    &.error {
      color: red;
    }

    font-size: 0.875rem;
    font-weight: 300;
    margin-bottom: 8px;
  }

  .tel__wrapper {
    top: 8px;
  }

  .error {
    border-color: red;
  }

  .tel__input {
    &.error {
      border-color: red;
    }

    width: 100%;
  }

  sub {
    position: relative;
    top: 4px;
  }

  width: 100%;
`
const StyledStandardInput = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: ${(props) => props.marginBottom};

  &.error {
    input {
      border-color: red;
    }

    label {
      color: red;
    }
  }

  label {
    font-size: 0.875rem;
    font-weight: 300;
    margin-bottom: 8px;
  }

  span {
    color: red;
  }

  input {
    width: 100%;
    height: 2.5rem;
    border: 1px solid rgb(174, 179, 201);
    border-radius: 3px;
    padding: 0px 1rem;
    font-size: 0.875rem;
    color: rgb(49, 55, 79);
    outline: none;
    background: none;
  }

  sub {
    margin-top: 4px;
  }
`

const StyledDatePickerInput = styled.div`
  margin-bottom: 1rem;
  width: 100%;

  &.error {
    .react-datepicker-wrapper .react-datepicker__input-container input {
      border-color: red;
    }

    label {
      color: red;
    }
  }

  .react-datepicker-wrapper {
    display: block;
    margin-top: 1rem;
    width: 100%;

    .react-datepicker__input-container {
      width: 100%;

      input {
        width: 100%;
        height: 2.5rem;
        border-radius: 3px;
        border: 1px solid #aeb3c9;
        padding: 0 1rem;
        outline: none;
      }
    }
  }

  label {
    font-size: 0.875rem;
    font-weight: 300;
    margin-bottom: 8px;
  }
`
